import {
    Alert,
    Button, Chip,
    Modal,
    ModalBody,
    ModalContent,
    ModalFooter,
    ModalHeader,
    Progress, Table, TableBody, TableCell, TableColumn,
    TableHeader,
    TableRow, Input, Card, CardBody, Divider, Skeleton
} from "@heroui/react";
import React, {useEffect, useState} from "react";
import {fetchClaimsDetailData} from "../../services/claimsService";
import { motion, AnimatePresence } from "framer-motion";
import {CartaReclamo, DetalleQuickview} from "./index";
import {format} from "date-fns";
import {toZonedTime} from "date-fns-tz";
import generateClaimLetter from "./CartaReclamoHelper";
import { generateCopiableName } from "../../utils/general";
import { Icon } from "@iconify/react";

export default ({ isOpen, onClose, selectedRows }) => {
    const [reclamoData, setReclamoData] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [view, setView] = useState("listado");
    const [rskRef, setRskRef] = useState(null);
    const [isBusy, setIsBusy] = useState(false);
    const [claimData, setClaimData] = useState(null); // Estado para los datos del reclamo

    // Función para manejar cuando se carguen los datos del reclamo
    const handleClaimDataLoaded = (data) => {
        setClaimData(data);
    };

    useEffect(() => {
        const fetchData = async () => {
            if (isOpen && selectedRows.length > 0) {
                setLoading(true);
                setError(null);

                try {
                    const claimId = selectedRows[0]?.id;
                    const data = await fetchClaimsDetailData("reclamosdetalle", claimId);
                    setReclamoData(data);
                } catch (err) {
                    console.error("Error al cargar los detalles del reclamo:", err);
                    setError("Error al cargar los datos.");
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchData();
    }, [isOpen, selectedRows]);

    const handleClose = () => {
        setRskRef(null);
        setView("listado");
        setClaimData(null); // Resetear datos del reclamo

        //función original de cierre del modal
        if (onClose) {
            onClose();
        }
    };

    async function handleGenerateDocument(rsk_ref) {
        if(selectedRows.length === 0) {
            alert("Ocurrió un error al generar el documento. Por favor, intenta de nuevo.");
            return;
        }

        try {
            await generateClaimLetter(rsk_ref, selectedRows[0]);
        } catch (error) {
            console.error("Error al generar el documento:", error);
            alert("Ocurrió un error al generar el documento. Por favor, intenta de nuevo.");
        }
    }

    const handleQuickViewClick = (rsk_ref) => {
        setRskRef(rsk_ref);
        setView("quickview");
    };

    return (
        <Modal isOpen={isOpen} size="full" onClose={handleClose} scrollBehavior="inside" isDismissable={false}>
            <ModalContent>
                {(onClose) => (
                    <>
                        <ModalHeader className="flex flex-col gap-1">
                            {view === "quickview" && !claimData ? (
                                <div className="flex items-center gap-2">
                                    <span>Loading...</span>
                                    <Skeleton className="h-6 w-64 rounded-lg" />
                                </div>
                            ) : view === "quickview" && claimData ? (
                                <span className="text-lg font-semibold">
                                    {claimData.quickview_claim ? generateCopiableName(
                                        claimData.quickview_claim.rsk_ref || "N/A", 
                                        claimData.quickview_claim.exporter_name || "N/A", 
                                        claimData.quickview_claim.vessel || "N/A", 
                                        claimData.maestra?.voyage_number || null, 
                                        claimData.quickview_claim.bl_awb || "N/A", 
                                        claimData.quickview_claim.container_number || "N/A", 
                                        claimData.quickview_claim.pol || "N/A", 
                                        claimData.quickview_claim.pod || "N/A", 
                                        claimData.quickview_claim_survey?.claimamount || 0,
                                        claimData.quickview_claim_survey?.claimamount || 0,
                                        claimData.quickview_claim_survey?.selectedcurrency || "$"
                                    ) : "Cargando datos..."}
                                    <button
                                        onClick={() => {
                                            if (claimData.quickview_claim) {
                                                const textToCopy = generateCopiableName(
                                                    claimData.quickview_claim.rsk_ref || "N/A", 
                                                    claimData.quickview_claim.exporter_name || "N/A", 
                                                    claimData.quickview_claim.vessel || "N/A", 
                                                    claimData.maestra?.voyage_number || null, 
                                                    claimData.quickview_claim.bl_awb || "N/A", 
                                                    claimData.quickview_claim.container_number || "N/A", 
                                                    claimData.quickview_claim.pol || "N/A", 
                                                    claimData.quickview_claim.pod || "N/A", 
                                                    claimData.quickview_claim_survey?.claimamount || 0,
                                                    claimData.quickview_claim_survey?.claimamount || 0,
                                                    claimData.quickview_claim_survey?.selectedcurrency || "$"
                                                );
                                                navigator.clipboard.writeText(textToCopy);
                                            }
                                        }}
                                        className="ml-2 p-1 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors duration-200"
                                        title="Copy to clipboard"
                                    >
                                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                                        </svg>
                                    </button>
                                </span>
                            ) : (
                                "Detalle de Reclamos"
                            )}
                        </ModalHeader>
                        <ModalBody>
                            <AnimatePresence mode="wait">
                                {view === "listado" && (
                                    <motion.div
                                        key="listado"
                                        initial={{ opacity: 0, y: -10 }}
                                        animate={{ opacity: 1, y: 0 }}
                                        exit={{ opacity: 0, y: 10 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        {selectedRows.length > 0 ? (
                                            selectedRows.map((row, index) => {
                                                const claimInfoFields = [
                                                    { 
                                                        icon: "lucide:hash", 
                                                        label: "ID Reclamo", 
                                                        value: row.id || "Sin referencia" 
                                                    },
                                                    { 
                                                        icon: "lucide:dollar-sign", 
                                                        label: "Total Reclamo", 
                                                        value: `$${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(row.total_reclamo_usd || 0)}` 
                                                    },
                                                    { 
                                                        icon: "lucide:calendar", 
                                                        label: "Fecha", 
                                                        value: row.created ? format(toZonedTime(new Date(row.created), "UTC"), "dd-MM-yyyy HH:mm:ss") : "N/A" 
                                                    },
                                                    { 
                                                        icon: "lucide:clock", 
                                                        label: "Fecha Expiración", 
                                                        value: row.expires_at ? format(toZonedTime(new Date(row.expires_at), "UTC"), "dd-MM-yyyy HH:mm:ss") : "N/A" 
                                                    },
                                                    { 
                                                        icon: "lucide:key", 
                                                        label: "Código Único", 
                                                        value: row.unique_code || "N/A" 
                                                    }
                                                ];

                                                return (
                                                    <motion.div
                                                        key={index}
                                                        className="mb-4"
                                                        initial={{ opacity: 0, x: -20 }}
                                                        animate={{ opacity: 1, x: 0 }}
                                                        transition={{ delay: index * 0.1 }}
                                                    >
                                                        <Card className="w-full">
                                                            <CardBody>
                                                                <h3 className="text-lg font-semibold mb-0">Información del Reclamo</h3>
                                                                <Divider className="my-4" />
                                                                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                                                                    {claimInfoFields.map((field, fieldIndex) => (
                                                                        <div key={fieldIndex} className="w-full">
                                                                            <Input
                                                                                isReadOnly
                                                                                label={field.label}
                                                                                value={field.value}
                                                                                startContent={
                                                                                    <Icon
                                                                                        icon={field.icon}
                                                                                        className="text-default-400 pointer-events-none flex-shrink-0"
                                                                                    />
                                                                                }
                                                                                classNames={{
                                                                                    label: "text-sm font-medium",
                                                                                    input: "text-sm"
                                                                                }}
                                                                            />
                                                                        </div>
                                                                    ))}
                                                                </div>
                                                            </CardBody>
                                                        </Card>
                                                    </motion.div>
                                                );
                                            })
                                        ) : (
                                            <p>No hay filas seleccionadas.</p>
                                        )}

                                        {/* Loader mientras carga */}
                                        {loading && (
                                            <motion.div
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.5 }}
                                            >
                                                <Progress isIndeterminate aria-label="Cargando..." className="max-w-full" size="sm" />
                                            </motion.div>
                                        )}

                                        {/* Tabla con HeroUI */}
                                        {!loading && reclamoData.length > 0 && (
                                            <motion.div
                                                initial={{ opacity: 0, scale: 1 }}
                                                animate={{ opacity: 1, scale: 1 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                <Table aria-label="Detalles de reclamos" className="mt-4" isStriped>
                                                    <TableHeader>
                                                        <TableColumn hidden>ID</TableColumn>
                                                        <TableColumn>RSK Ref.</TableColumn>
                                                        <TableColumn>Embarcador</TableColumn>
                                                        <TableColumn>BL/AWB</TableColumn>
                                                        <TableColumn>Reclamo USD</TableColumn>
                                                        <TableColumn>Opciones</TableColumn>
                                                    </TableHeader>
                                                    <TableBody>
                                                        {reclamoData.map((item, index) => (
                                                            <TableRow
                                                                key={index}
                                                                initial={{ opacity: 0, y: 10 }}
                                                                animate={{ opacity: 1, y: 0 }}
                                                                transition={{ delay: index * 0.05 }}
                                                            >
                                                                <TableCell hidden>{item.id}</TableCell>
                                                                <TableCell>{item.rsk_ref || "Sin RSK Ref."}</TableCell>
                                                                <TableCell>{item.embarcador || "Sin Embarcador"}</TableCell>
                                                                <TableCell>{item.bl_awb ? item.bl_awb.replace(/-/g, '') : "Sin BL/AWB"}</TableCell>
                                                                <TableCell>
                                                                    ${new Intl.NumberFormat('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(item.reclamo_usd || 0)}
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Chip
                                                                        color="success"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            handleQuickViewClick(item.rsk_ref)
                                                                        }}
                                                                        role="button"
                                                                        className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all mb-1 mr-1"
                                                                    >
                                                                        QuickView
                                                                    </Chip>

                                                                    {/* <Chip
                                                                        color="primary"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            // setRskRef(item.id)
                                                                            // setView("documentos")
                                                                        }}
                                                                        role="button"
                                                                        className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all mb-1 mr-1"
                                                                    >
                                                                        Editar
                                                                    </Chip> */}

                                                                    <Chip
                                                                        color="primary"
                                                                        size="sm"
                                                                        onClick={() => {
                                                                            handleGenerateDocument(item.rsk_ref)
                                                                        }}
                                                                        role="button"
                                                                        className="cursor-pointer hover:bg-opacity-80 active:scale-95 transition-all mb-1 mr-1"
                                                                    >
                                                                        Carta Reclamo
                                                                    </Chip>

                                                                </TableCell>
                                                            </TableRow>
                                                        ))}
                                                    </TableBody>
                                                </Table>
                                            </motion.div>
                                        )}

                                        {error && (
                                            <motion.p
                                                className="text-red-500"
                                                initial={{ opacity: 0 }}
                                                animate={{ opacity: 1 }}
                                                transition={{ duration: 0.3 }}
                                            >
                                                {error}
                                            </motion.p>
                                        )}
                                    </motion.div>
                                )}

                                {view === "quickview" && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 1 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >

                                        <DetalleQuickview
                                            rskRef={rskRef}
                                            isBusy={isBusy}
                                            setIsBusy={setIsBusy}
                                            onDataLoaded={handleClaimDataLoaded}
                                        />

                                    </motion.div>
                                )}
                                {view === "documentos" && (
                                    <motion.div
                                        initial={{ opacity: 0, scale: 1 }}
                                        animate={{ opacity: 1, scale: 1 }}
                                        transition={{ duration: 0.3 }}
                                    >
                                        <h1>documentos</h1>
                                        <p>{rskRef}</p>
                                    </motion.div>
                                )}
                            </AnimatePresence>
                        </ModalBody>
                        <ModalFooter>
                            <Button
                                isDisabled={!rskRef}
                                color="primary"
                                variant="light"
                                onPress={() => { 
                                    setView("listado");
                                    setRskRef(null);
                                    setClaimData(null); // Resetear datos del reclamo
                                }}
                                isLoading={isBusy}
                            >Volver</Button>
                            <Button
                                isLoading={isBusy}
                                color="danger"
                                variant="light"
                                onPress={() => {
                                    handleClose()
                                }}
                            >
                                Cerrar
                            </Button>
                        </ModalFooter>
                    </>
                )}
            </ModalContent>
        </Modal>
    );
}
